import React, { useEffect, useCallback } from 'react';
import { isDesktop, isIOS } from 'react-device-detect';

import { sendCustomSentryBreadcrumb } from '@helpers/sentry';
import { useFullscreen } from '@core/dashboard/hooks/fullscreen';

import FullScreenDrawer, { useDrawer } from '../../FullScreenDrawer';
import FullScreenButtonView from './FullScreenButtonView';
import { NavigatorStandalone } from './interfaces';
import { getClientSpecificFullScreenHelperVisible } from '@helpers/clientSpecificUtils';

const isHelperVisible = getClientSpecificFullScreenHelperVisible();

const FullScreenButton = () => {
  const { toggleFullscreen, isFullscreen } = useFullscreen();

  const { open: isOpenDrawer, toggleDrawer } = useDrawer();

  const handleFullscreenClick = useCallback(() => {
    sendCustomSentryBreadcrumb({ message: 'show in full screen' });
    toggleFullscreen();
  }, [toggleFullscreen]);

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'F11') {
        event.preventDefault();
        handleFullscreenClick();
      }
      // Shift-Command-F in macOS Chrome is used to hide address bar and open full screen
      if (event.code === 'KeyF' && event.metaKey && event.shiftKey) {
        event.preventDefault();
        handleFullscreenClick();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleFullscreenClick]);

  const handleMobileDrawer = () => {
    sendCustomSentryBreadcrumb({ message: 'show in full tutorial' });
    toggleDrawer();
  };

  if (isDesktop) {
    return (
      <FullScreenButtonView
        isFullscreen={isFullscreen}
        onClick={handleFullscreenClick}
      />
    );
  }

  if (
    !isHelperVisible ||
    (isIOS && (window.navigator as NavigatorStandalone)?.standalone) ||
    (!isIOS && window.matchMedia('(display-mode: standalone)').matches)
  ) {
    return null;
  }

  return (
    <>
      <FullScreenButtonView onClick={handleMobileDrawer} />
      <FullScreenDrawer isOpen={isOpenDrawer} onClick={handleMobileDrawer} />
    </>
  );
};

export default FullScreenButton;
